import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, ChangeDetectorRef, HostListener } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { ConfigService } from 'src/app/services/config.service';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocumentProxy } from 'pdfjs-dist';

@Component({
    selector: 'app-preview-widget',
    templateUrl: './preview-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './preview-widget.component.scss'],
    standalone: false
})
export class PreviewWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  @Input() path: string = '';
  @Input() previewType: 'image' | 'pdf' = 'image';

  @ViewChild('pdfContainer') pdfContainer: ElementRef<HTMLDivElement>;

  public loading: boolean = true;
  public error: string | null = null;
  public imageUrl: SafeResourceUrl | null = null;
  public filename: string = '';

  // PDF state
  private pdfDoc: PDFDocumentProxy | null = null;
  private pdfData: ArrayBuffer | null = null;
  public pdfScale: number = 1.0;
  public pdfTotalPages: number = 0;
  public pdfCurrentPage: number = 1;

  // Zoom presets
  public readonly ZOOM_MIN = 0.25;
  public readonly ZOOM_MAX = 4.0;
  public readonly ZOOM_STEP = 0.25;

  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    super();
    pdfjsLib.GlobalWorkerOptions.workerSrc = 'assets/pdf.worker.min.mjs';
  }

  ngOnInit(): void {
    this.filename = this.path.split('/').pop() || 'Preview';
    this.loadContent();
  }

  ngOnDestroy(): void {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
    }
    super.ngOnDestroy();
  }

  private loadContent(): void {
    const isUploadUrl = this.path.startsWith('/mux/') || this.path.startsWith('http');
    const url = isUploadUrl
      ? this.path
      : `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(this.path)}`;

    if (this.previewType === 'pdf') {
      this.http.get(url, { responseType: 'arraybuffer' }).subscribe({
        next: async (data) => {
          this.pdfData = data;
          await this.initPdf(data);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load PDF:', err);
          this.error = 'Failed to load PDF';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    } else {
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          const blobUrl = URL.createObjectURL(blob);
          this.imageUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('Failed to load image:', err);
          this.error = 'Failed to load image';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  private async initPdf(data: ArrayBuffer): Promise<void> {
    try {
      this.pdfDoc = await pdfjsLib.getDocument({ data }).promise;
      this.pdfTotalPages = this.pdfDoc.numPages;
      this.cdr.detectChanges();

      // Wait for container to be rendered, then fit to width
      setTimeout(async () => {
        await this.fitToWidth();
      }, 0);
    } catch (err) {
      console.error('Error initializing PDF:', err);
      this.error = 'Failed to render PDF';
      this.cdr.detectChanges();
    }
  }

  private async fitToWidth(): Promise<void> {
    if (!this.pdfDoc || !this.pdfContainer?.nativeElement) {
      // Container not ready, render at default scale
      await this.renderAllPages();
      return;
    }

    // Get first page to determine dimensions
    const page = await this.pdfDoc.getPage(1);
    const viewport = page.getViewport({ scale: 1.0 });

    // Calculate scale to fit container width (minus padding)
    const containerWidth = this.pdfContainer.nativeElement.clientWidth - 32;
    this.pdfScale = containerWidth / viewport.width;

    // Clamp to reasonable bounds
    this.pdfScale = Math.max(this.ZOOM_MIN, Math.min(this.ZOOM_MAX, this.pdfScale));

    await this.renderAllPages();
  }

  private async renderAllPages(): Promise<void> {
    if (!this.pdfDoc || !this.pdfContainer?.nativeElement) return;

    const container = this.pdfContainer.nativeElement;
    container.innerHTML = '';

    for (let pageNum = 1; pageNum <= this.pdfDoc.numPages; pageNum++) {
      const page = await this.pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: this.pdfScale });

      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d')!;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({
        canvasContext: context,
        viewport: viewport,
        canvas: canvas
      }).promise;

      container.appendChild(canvas);
    }

    this.cdr.detectChanges();
  }

  // Zoom controls
  async zoomIn(): Promise<void> {
    if (this.pdfScale < this.ZOOM_MAX) {
      this.pdfScale = Math.min(this.ZOOM_MAX, this.pdfScale + this.ZOOM_STEP);
      await this.renderAllPages();
    }
  }

  async zoomOut(): Promise<void> {
    if (this.pdfScale > this.ZOOM_MIN) {
      this.pdfScale = Math.max(this.ZOOM_MIN, this.pdfScale - this.ZOOM_STEP);
      await this.renderAllPages();
    }
  }

  async zoomFit(): Promise<void> {
    await this.fitToWidth();
  }

  async zoomActual(): Promise<void> {
    this.pdfScale = 1.0;
    await this.renderAllPages();
  }

  get zoomPercent(): number {
    return Math.round(this.pdfScale * 100);
  }
}
